import { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiClient } from '../services/http.js';

const AuthContext = createContext(null);

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (!context) throw new Error('useAuth must be used inside AuthProvider');
    return context;
};

export const AuthProvider = ({ children }) => {
    const [user, setUser] = useState(null);
    const [isAuthenticated, setIsAuthenticated] = useState(false);
    // true até a primeira resolução de /auth/session: evita que os guards de rota
    // redirecionem para /login um usuário que só ainda não foi validado (reload/deep link).
    const [isLoading, setIsLoading] = useState(true);
    const [termsAcceptanceRequired, setTermsAcceptanceRequired] = useState(false);
    const [onboardingRequired, setOnboardingRequired] = useState(false);
    const navigate = useNavigate();

    const refreshSession = async () => {
        try {
            const { res, data } = await apiClient.getSession();
            if (!res.ok) {
                setUser(null);
                setIsAuthenticated(false);
                setTermsAcceptanceRequired(false);
                setOnboardingRequired(false);
                return;
            }
            setUser(data?.user || null);
            setIsAuthenticated(Boolean(data?.authenticated));
            setTermsAcceptanceRequired(Boolean(data?.terms_acceptance_required));
            setOnboardingRequired(Boolean(data?.onboarding_required));
        } catch {
            setUser(null);
            setIsAuthenticated(false);
            setTermsAcceptanceRequired(false);
            setOnboardingRequired(false);
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        refreshSession();

        const handleAuthExpired = () => {
            setUser(null);
            setIsAuthenticated(false);
            setTermsAcceptanceRequired(false);
            setOnboardingRequired(false);
            navigate('/login', { state: { reason: 'expired' } });
        };

        window.addEventListener('auth:expired', handleAuthExpired);
        return () => window.removeEventListener('auth:expired', handleAuthExpired);
    }, [navigate]);

    const login = async (email, password) => {
        try {
            const { res } = await apiClient.login({ email, password });
            if (!res.ok) return false;

            const session = await apiClient.getSession();
            if (!session.res.ok) return false;

            setUser(session.data?.user || null);
            setIsAuthenticated(Boolean(session.data?.authenticated));
            setTermsAcceptanceRequired(Boolean(session.data?.terms_acceptance_required));
            setOnboardingRequired(Boolean(session.data?.onboarding_required));
            return Boolean(session.data?.authenticated);
        } catch {
            return false;
        }
    };

    const logout = async () => {
        await apiClient
            .logout()
            .catch(() => null)
            .finally(() => {
                setUser(null);
                setIsAuthenticated(false);
                setTermsAcceptanceRequired(false);
                setOnboardingRequired(false);
            });
    };

    const value = useMemo(() => ({
        user,
        isAuthenticated,
        isLoading,
        termsAcceptanceRequired,
        onboardingRequired,
        login,
        logout,
        refreshSession
    }), [user, isAuthenticated, isLoading, termsAcceptanceRequired, onboardingRequired]);

    return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
